<template>
  <div class="conditional-editor">
    <!-- Toolbar -->
    <div class="toolbar">
      <div class="toolbar-section">
        <h5 class="mb-0">
          <i class="fas fa-project-diagram me-2"></i>
          Conditional Test Editor <span class="ctrlScroll">(Ctrl Scroll to zoom)</span>
        </h5>
      </div>

      <div class="toolbar-section toolbar-actions">
        <button class="btn btn-sm btn-success me-2" @click="createStartNode" :disabled="hasStartNode"
          title="Add a starting node">
          <i class="fas fa-play me-1"></i> Start
        </button>

        <button class="btn btn-sm btn-primary me-2" @click="addNode('question')" title="Add a question"
          :disabled="showCreateModal || isCreatingNode">
          <i class="fas fa-question-circle me-1"></i>
          Question
        </button>

        <button style="color:white" class="btn btn-sm btn-info me-2" @click="addPage()" title="Add a Page"
          :disabled="showCreateModal || isCreatingNode">
          <i class="fa-solid fa-file-circle-plus"></i>
          Page
        </button>



        <button class="btn btn-sm btn-secondary me-2" @click="createSwitcherNode" title="Add a switcher">
          <i class="fas fa-random me-1"></i> Switcher
        </button>


        <button class="btn btn-sm btn-warning me-2" @click="createTerminationNode" title="Add a end node">
          <i class="fas fa-flag-checkered me-1"></i> Terminaison
        </button>

        <div class="toolbar-divider"></div>

        <button class="btn btn-sm btn-outline-primary me-2" @click="toggleItemBank" title="Import from Item Library">
          <img src="@/assets/Import2ItemLib.png" alt="import" style="height: 18px; width: 18px; margin-right: 4px;" />
          Item Library
        </button>

        <div class="toolbar-divider"></div>

        <button class="btn btn-sm btn-outline-secondary me-2" @click="openDisplaySettings" title="Test Display Settings">
          <i class="fas fa-palette me-1"></i>
          Test Display
        </button>

        <div class="toolbar-divider"></div>

        <button hidden class="btn btn-sm btn-info me-2" @click="validateGraph" title="Validate the path">
          <i class="fas fa-check-circle me-1"></i>
          Validate
        </button>

        <button hidden class="btn btn-sm btn-warning me-2" @click="cleanUnconnectedNodes"
          title="Supprimer les nœuds non connectés">
          <i class="fas fa-broom me-1"></i>
          <span v-if="unconnectedNodesCount > 0" class="badge bg-danger ms-1">{{ unconnectedNodesCount }}</span>
          Nettoyer
        </button>

        <button hidden class="btn btn-sm btn-secondary me-2" @click="autoLayout" title="Automatic reorder">
          <i class="fas fa-magic me-1"></i>
          Auto-layout
        </button>

        <!-- <button class="btn btn-sm btn-dark" @click="saveStructure" :disabled="isSaving" title="Sauvegarder le parcours">
          <i class="fas fa-save me-1"></i>
          {{ isSaving ? 'Sauvegarde...' : 'Sauvegarder' }}
        </button> -->
      </div>
    </div>

    <!-- Vue Flow Canvas -->
    <div class="flow-container">
      <VueFlow v-model:nodes="nodes" v-model:edges="edges" :default-viewport="viewport" :min-zoom="0.2" :max-zoom="4"
        :pan-on-scroll="false" :zoom-on-scroll="true" :prevent-scrolling="false"
        fit-view-on-init @nodes-change="onNodesChange" @edges-change="onEdgesChange" @connect="onConnect"
        @edge-update="onEdgeUpdate" @node-click="onNodeClick" @edge-click="onEdgeClick" @pane-click="onPaneClick">
        <!-- Background pattern -->
        <Background pattern-color="#aaa" :gap="16" />

        <!-- Minimap -->
        <MiniMap />

        <!-- Custom node templates -->
        <template #node-start="nodeProps">
          <StartNode v-bind="nodeProps" @delete="deleteNode" />
        </template>

        <template #node-question="nodeProps">
          <QuestionNode v-bind="nodeProps" :available-elements="testData.elements" @delete="deleteNode"
            @update="updateNode" />
        </template>


        <template #node-choice="nodeProps">
          <ChoiceNode v-bind="nodeProps" :parent-node="nodeProps.parentNodeId" :element="getElementById(nodeProps.data?.elementId)" @delete="deleteNode"
            @update="updateNode" @open-editor="openChoiceEditor" />
          <div class="small mt-1" v-if="!nodeProps.data?.elementId">
            <span class="badge bg-warning text-dark">incomplet</span>
          </div>
        </template>

        <template #node-choiceBlock="nodeProps">
          <ChoiceBlockNode v-bind="nodeProps" :parent-node="nodeProps.parentNodeId" :element="getElementById(nodeProps.data?.elementId)" @delete="deleteNode"
            @update="updateNode" @open-editor="openChoiceBlockEditor" />
          <div class="small mt-1" v-if="!nodeProps.data?.elementId">
            <span class="badge bg-warning text-dark">incomplet</span>
          </div>
        </template>

        <template #node-message="nodeProps">
          <MessageNode v-bind="nodeProps" :parent-node="nodeProps.parentNodeId" :element="getElementById(nodeProps.data?.elementId)" @delete="deleteNode"
            @update="updateNode" @open-editor="openMessageEditor" />
          <div class="small mt-1" v-if="!nodeProps.data?.elementId">
            <span class="badge bg-warning text-dark">incomplet</span>
          </div>
        </template>

        <template #node-order="nodeProps">
          <OrderNode v-bind="nodeProps" :parent-node="nodeProps.parentNodeId" :element="getElementById(nodeProps.data?.elementId)" @delete="deleteNode"
            @update="updateNode" @open-editor="openOrderEditor" />
          <div class="small mt-1" v-if="!nodeProps.data?.elementId">
            <span class="badge bg-warning text-dark">incomplet</span>
          </div>
        </template>

        <template #node-shortAnswer="nodeProps">
          <ShortAnswerNode v-bind="nodeProps" :parent-node="nodeProps.parentNodeId" :element="getElementById(nodeProps.data?.elementId)" @delete="deleteNode"
            @update="updateNode" @open-editor="openShortAnswerEditor" />
          <div class="small mt-1" v-if="!nodeProps.data?.elementId">
            <span class="badge bg-warning text-dark">incomplet</span>
          </div>
        </template>

        <template #node-textGaps="nodeProps">
          <TextGapsNode v-bind="nodeProps" :parent-node="nodeProps.parentNodeId" :element="getElementById(nodeProps.data?.elementId)" @delete="deleteNode"
            @update="updateNode" @open-editor="openTextGapsEditor" />
          <div class="small mt-1" v-if="!nodeProps.data?.elementId">
            <span class="badge bg-warning text-dark">incomplet</span>
          </div>
        </template>

        <template #node-textPuzzle="nodeProps">
          <TextPuzzleNode v-bind="nodeProps" :parent-node="nodeProps.parentNodeId" :element="getElementById(nodeProps.data?.elementId)" @delete="deleteNode"
            @update="updateNode" @open-editor="openTextPuzzleEditor" />
          <div class="small mt-1" v-if="!nodeProps.data?.elementId">
            <span class="badge bg-warning text-dark">incomplet</span>
          </div>
        </template>

        <template #node-makingPairs="nodeProps">
          <MakingPairsNode v-bind="nodeProps" :parent-node="nodeProps.parentNodeId" :element="getElementById(nodeProps.data?.elementId)" @delete="deleteNode"
            @update="updateNode" @open-editor="openMakingPairsEditor" />
          <div class="small mt-1" v-if="!nodeProps.data?.elementId">
            <span class="badge bg-warning text-dark">incomplet</span>
          </div>
        </template>

        <template #node-match="nodeProps">
          <MatchNode v-bind="nodeProps" :parent-node="nodeProps.parentNodeId" :element="getElementById(nodeProps.data?.elementId)" @delete="deleteNode"
            @update="updateNode" @open-editor="openMatchEditor" />
          <div class="small mt-1" v-if="!nodeProps.data?.elementId">
            <span class="badge bg-warning text-dark">incomplet</span>
          </div>
        </template>

        <template #node-hotSpot="nodeProps">
          <HotSpotNode v-bind="nodeProps" :parent-node="nodeProps.parentNodeId" :element="getElementById(nodeProps.data?.elementId)" @delete="deleteNode"
            @update="updateNode" @open-editor="openHotSpotEditor" />
          <div class="small mt-1" v-if="!nodeProps.data?.elementId">
            <span class="badge bg-warning text-dark">incomplet</span>
          </div>
        </template>

        <template #node-termination="nodeProps">
          <TerminationNode v-bind="nodeProps" @delete="deleteNode" @update="updateNode" />
        </template>

        <template #node-page="nodeProps">
          <PageNode v-bind="nodeProps" :children-count="getChildrenCount(nodeProps.id)" @delete="deletePageNode"
            @edit="openPageEditor" />
        </template>

        <template #node-switcher="nodeProps">
          <SwitcherNode v-bind="nodeProps" @delete="deleteNode" @update="updateNode" />
        </template>
      </VueFlow>

      <!-- Custom Zoom Controls -->
      <div class="custom-controls">
        <button class="control-btn" @click="zoomIn" title="Zoom in">
          <i class="fas fa-plus"></i>
        </button>
        <button class="control-btn" @click="zoomOut" title="Zoom out">
          <i class="fas fa-minus"></i>
        </button>
        <button class="control-btn" @click="fitView" title="Fit view">
          <i class="fas fa-expand"></i>
        </button>
        <button class="control-btn" @click="resetZoom" title="Reset zoom">
          <i class="fas fa-compress"></i>
        </button>
      </div>
    </div>

    <!-- Validation Results Modal -->
    <div v-if="validationResults" class="modal show d-block" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i
                :class="validationResults.isValid ? 'fas fa-check-circle text-success' : 'fas fa-exclamation-triangle text-warning'"></i>
              Résultats de validation
            </h5>
            <button type="button" class="btn-close" @click="validationResults = null"></button>
          </div>
          <div class="modal-body">
            <div v-if="validationResults.isValid" class="alert alert-success">
              <i class="fas fa-check-circle me-2"></i>
              Le parcours conditionnel est valide !
            </div>

            <div v-if="validationResults.errors?.length > 0" class="mb-3">
              <h6 class="text-danger">Erreurs :</h6>
              <ul class="list-unstyled">
                <li v-for="(error, i) in validationResults.errors" :key="i" class="text-danger">
                  <i class="fas fa-times-circle me-2"></i>{{ error }}
                </li>
              </ul>
            </div>

            <div v-if="validationResults.warnings?.length > 0" class="mb-3">
              <h6 class="text-warning">Avertissements :</h6>
              <ul class="list-unstyled">
                <li v-for="(warning, i) in validationResults.warnings" :key="i" class="text-warning">
                  <i class="fas fa-exclamation-triangle me-2"></i>{{ warning }}
                </li>
              </ul>
            </div>

            <div v-if="validationResults.stats">
              <h6>Statistiques :</h6>
              <ul class="list-unstyled">
                <li><strong>Nœuds totaux :</strong> {{ validationResults.stats.totalNodes }}</li>
                <li><strong>Connexions :</strong> {{ validationResults.stats.totalEdges }}</li>
                <li><strong>Questions :</strong> {{ validationResults.stats.questionNodes }}</li>
                <li><strong>Terminaisons :</strong> {{ validationResults.stats.terminationNodes }}</li>
              </ul>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="validationResults = null">Close</button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="validationResults" class="modal-backdrop show"></div>


    <div v-if="activeEditor" class="bottom-panel">
      <div class="bottom-panel-header">
        <strong>Editor {{ activeEditor.type }}</strong>
        <button class="btn btn-sm btn-outline-secondary" @click="closeEditor">Close</button>
      </div>

      <div class="bottom-panel-content row g-0">
        <div class="col-6 editor-column">
          <component v-if="editorRegistry[activeEditor.type]?.editor"
            :is="editorRegistry[activeEditor.type].editor"
            :element-id="activeEditor.elementId"
            :node-id="activeEditor.nodeId"
            :element-type="activeEditor.type ?? ''"
            @updated="onEditorUpdated" />
          <div v-else class="alert alert-warning">
            Éditeur non disponible pour le type: {{ activeEditor.type }}
          </div>
        </div>

        <div class="col-6 preview-column">
          <component v-if="editorRegistry[activeEditor.type]?.preview"
            :is="editorRegistry[activeEditor.type].preview"
            :element="getElementById(activeEditor.elementId)" />
          <div v-else class="alert alert-warning">
            Prévisualisation non disponible
          </div>
        </div>
      </div>
    </div>


  </div>

  <!-- Teleport (remplace ton bloc actuel) -->
  <Teleport to="body">
    <div v-if="showCreateModal" class="modal-backdrop" @click.self="onCreateCancel">
      <div class="modal-card" role="dialog" aria-modal="true">
        <header class="modal-header">
          <h5 class="mb-0">Créer / sélectionner l'item</h5>
          <button class="btn-close" @click="onCreateCancel" aria-label="Close">×</button>
        </header>

        <section class="modal-body">
          <TabsComponent :key="modalKey" context="conditional"
            :test-id="props.testData._id || props.testData.ID" :auto-create="true" @confirm="onCreateConfirm" @cancel="onCreateCancel" />
        </section>
      </div>
    </div>
  </Teleport>

  <!-- Modal PageEditor -->
  <Teleport to="body">
    <div v-if="showPageEditor && selectedPageForEdit" class="modal-backdrop" @click.self="onPageEditorCancel">
      <div class="modal-card modal-lg" role="dialog" aria-modal="true">
        <header class="modal-header">
          <h4 class="mb-0">📘 Page Editor</h4>
          <button class="btn-close" @click="onPageEditorCancel" aria-label="Close">×</button>
        </header>

        <section class="modal-body">
          <PageEditor :page-node="selectedPageForEdit" :all-nodes="nodes" :test-data="props.testData"
            @save="onPageEditorSave" @cancel="onPageEditorCancel" @update:nodes="onPageNodesUpdate" />
        </section>
      </div>
    </div>
  </Teleport>

  <!-- Modal Item Bank -->
  <Teleport to="body">
    <div v-if="showItemBank" class="modal-backdrop" @click.self="showItemBank = false">
      <itemBankgrabber
        :is-visible="showItemBank"
        selection-mode="click"
        @close="showItemBank = false"
        @item-selected="handleItemBankImport"
      />
    </div>

    <!-- Display Settings Modal -->
    <ConditionalDisplaySettings
      v-model="showDisplaySettings"
      :current-settings="displaySettings"
      @save="saveDisplaySettings"
    />
  </Teleport>

</template>


<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import { VueFlow, useVueFlow } from '@vue-flow/core'
import { Background } from '@vue-flow/background'
import { MiniMap } from '@vue-flow/minimap'
import { v4 as uuidv4 } from 'uuid'
import api from '@/services/axios'


// Nodes (utilisés dans les slots du template)
import StartNode from './nodes/StartNode.vue'
import QuestionNode from './nodes/QuestionNode.vue'
import TerminationNode from './nodes/TerminationNode.vue'
import PageNode from './nodes/PageNode.vue'
import EdgeConditionEditor from './EdgeConditionEditor.vue'
import SwitcherNode from './nodes/SwitcherNode.vue'
import ChoiceNode from './nodes/ChoiceNode.vue'
import ChoiceBlockNode from './nodes/ChoiceBlockNode.vue'

import TabsComponent from '@/components/interactions/default/tabCreator/TabsComponent.vue'
import itemBankgrabber from '@/components/testTabSystem/itemBankgrabber.vue'
// Page Editor
import PageEditor from './PageEditor.vue'

// Editor / Preview pour "choice"
import ChoiceEditor from '@/components/interactions/choice/Editor.vue'
import ChoicePreview from '@/components/interactions/choice/Preview.vue'

// Editor / Preview pour "choiceBlock"
import ChoiceBlockEditor from '@/components/interactions/choiceBlock/Editor.vue'
import ChoiceBlockPreview from '@/components/interactions/choiceBlock/Preview.vue'

// Editor / Preview pour "message"
import MessageEditor from '@/components/interactions/message/Editor.vue'
import ConditionalDisplaySettings from './ConditionalDisplaySettings.vue'
import MessagePreview from '@/components/interactions/message/Preview.vue'

// Node pour "message"
import MessageNode from './nodes/MessageNode.vue'

// Editor / Preview pour "order"
import OrderEditor from '@/components/interactions/order/Editor.vue'
import OrderPreview from '@/components/interactions/order/Preview.vue'

// Node pour "order"
import OrderNode from './nodes/OrderNode.vue'

// Editor / Preview pour "shortAnswer"
import ShortAnswerEditor from '@/components/interactions/shortAnswer/Editor.vue'
import ShortAnswerPreview from '@/components/interactions/shortAnswer/Preview.vue'

// Node pour "shortAnswer"
import ShortAnswerNode from './nodes/ShortAnswerNode.vue'

// Editor / Preview pour "textGaps"
import TextGapsEditor from '@/components/interactions/textGaps/Editor.vue'
import TextGapsPreview from '@/components/interactions/textGaps/Preview.vue'

// Node pour "textGaps"
import TextGapsNode from './nodes/TextGapsNode.vue'

// Editor / Preview pour "textPuzzle"
import TextPuzzleEditor from '@/components/interactions/textPuzzle/Editor.vue'
import TextPuzzlePreview from '@/components/interactions/textPuzzle/Preview.vue'

// Node pour "textPuzzle"
import TextPuzzleNode from './nodes/TextPuzzleNode.vue'

// Editor / Preview pour "makingPairs"
import MakingPairsEditor from '@/components/interactions/makingPairs/Editor.vue'
import MakingPairsPreview from '@/components/interactions/makingPairs/Preview.vue'

// Node pour "makingPairs"
import MakingPairsNode from './nodes/MakingPairsNode.vue'

// Editor / Preview pour "match"
import MatchEditor from '@/components/interactions/match/Editor.vue'
import HotSpotEditor from '@/components/interactions/hotSpot/Editor.vue'
import MatchPreview from '@/components/interactions/match/Preview.vue'
import HotSpotPreview from '@/components/interactions/hotSpot/Preview.vue'

// Node pour "match"
import MatchNode from './nodes/MatchNode.vue'

// Node pour "hotSpot"
import HotSpotNode from './nodes/HotSpotNode.vue'

// Store test (pour garder l'élément sélectionné, etc.)
import { useTestStore } from '@/stores/testStore'
import { storeToRefs } from 'pinia'

// -------------------- Props & Emits --------------------
const props = defineProps({
  showItemBank: { type: Boolean, default: false },
  testData: { type: Object, required: true }
})
const emit = defineEmits(['update:structure', 'toggleItemBank'])

// -------------------- VueFlow helpers --------------------
const { fitView, zoomIn, zoomOut, zoomTo, getViewport, addNodes, findNode, setNodes, setEdges, removeNodes, removeEdges, applyEdgeChanges, applyNodeChanges, onConnectStart, onConnectEnd: vueFlowOnConnectEnd } = useVueFlow()

// -------------------- State --------------------
const nodes = ref([])                // v-model:nodes
const edges = ref([])                // v-model:edges
const viewport = ref({ x: 0, y: 0, zoom: 1 })
const selectedNode = ref(null)
const selectedEdge = ref(null)
const connectionInProgress = ref(null) // Pour tracker la connexion en cours
const validationResults = ref(null)
const isSaving = ref(false)

const isLoadingStructure = ref(false)
const isSavingStructure = ref(false)

const lastCreatedNodeId = ref(null)
const deletedNodeIds = ref([]) // IDs des nodes supprimés à ignorer (tableau pour réactivité)

const showCreateModal = ref(false)
const pendingType = ref(null)
const modalKey = ref(0)

// Item Bank
const showItemBank = ref(false)

function toggleItemBank() {
  showItemBank.value = !showItemBank.value
}

function openDisplaySettings() {
  // Load existing settings from testData if available
  if (props.testData.displaySettings) {
    displaySettings.value = { ...displaySettings.value, ...props.testData.displaySettings }
  }
  showDisplaySettings.value = true
}

async function saveDisplaySettings(settings) {
  console.log('🎨 [saveDisplaySettings] Début sauvegarde:', settings)
  displaySettings.value = settings
  // Update testData with new display settings
  if (props.testData) {
    props.testData.displaySettings = settings
    console.log('🎨 [saveDisplaySettings] props.testData.displaySettings mis à jour:', props.testData.displaySettings)
    emit('update:structure', {
      ...props.testData,
      displaySettings: settings
    })
    // Trigger auto-save
    await saveStructure()
    console.log('✅ [saveDisplaySettings] Display settings saved and structure saved:', settings)
  }
}

/**
 * Construit un node pour la structure conditionnelle à partir d'un élément
 */
function buildConditionalNodeFromElement(element) {
  const el_Type = element.el_Type
  const el_ID = element.el_ID
  const el_Label = element.el_Label || element.el_Text || 'Nouvelle question'

  // Déterminer le type de rendu pour VueFlow
  const renderType = (el_Type === 'start' || el_Type === 'termination' || el_Type === 'choice')
    ? el_Type
    : 'question' // fallback

  const data = {
    elementId: el_ID,
    label: el_Label,
    mode: renderType === 'question' ? 'options' : undefined,
  }

  // Si c'est un sous-type de question
  if (renderType === 'question' && el_Type !== 'question' && el_Type !== 'choice') {
    data.subtype = el_Type
  }

  // Position centrée sur le viewport
  const centerPos = getCenterPosition()

  const node = {
    id: uuidv4(),
    type: renderType,
    position: {
      x: centerPos.x + Math.random() * 100 - 50,
      y: centerPos.y + Math.random() * 100 - 50,
    },
    collapsed: false,
    style: { width: 280, height: 140 },
    data,
  }

  return node
}

/**
 * Gère l'import d'un item depuis la bibliothèque d'items
 */
async function handleItemBankImport(itemId) {
  console.log('📦 [handleItemBankImport] Début import item:', itemId)

  const testId = props.testData._id || props.testData.ID
  if (!testId) {
    console.error('❌ [handleItemBankImport] testId manquant')
    alert('Erreur: ID du test manquant')
    return
  }

  try {
    // 1) Récupérer l'item complet depuis la collection
    console.log('📦 [handleItemBankImport] Récupération item via GET /items/full/' + itemId)
    const itemResponse = await api.get(`/items/full/${itemId}`)
    const item = itemResponse.data

    if (!item || !item.el_Type) {
      console.error('❌ [handleItemBankImport] Item invalide:', item)
      alert('Erreur: Item invalide')
      return
    }

    console.log('📦 [handleItemBankImport] Item récupéré:', item.el_Label || item.el_Text)

    // 2) Créer l'élément dans le test via POST atomique
    console.log('📦 [handleItemBankImport] Création élément via POST /tests/' + testId + '/elements')

    // Copier TOUS les champs de l'item (important pour textGaps, hotSpot, etc.)
    const payload = {
      ...item, // Copie tous les champs spécifiques (el_GapsedText, gaps, zones, etc.)
      metadata: {
        ...(item.metadata || {}),
        source: 'ItemLibrary',
        original_id: itemId
      },
    }

    // Supprimer les champs qui seront recréés par le backend
    delete payload._id
    delete payload.el_ID

    const createResponse = await api.post(`/tests/${testId}/elements`, payload)
    const newElementId = createResponse.data?.el_ID

    if (!newElementId) {
      console.error('❌ [handleItemBankImport] el_ID absent dans la réponse')
      alert('Erreur lors de la création de l\'élément')
      return
    }

    console.log('✅ [handleItemBankImport] Élément créé avec ID:', newElementId)

    // 3) Construire le node
    const newElement = createResponse.data?.element || { ...item, el_ID: newElementId }
    const newNode = buildConditionalNodeFromElement(newElement)
    console.log('📦 [handleItemBankImport] Node construit:', newNode)

    // 4) Récupérer la structure actuelle
    console.log('📦 [handleItemBankImport] Récupération structure actuelle')
    const structureResponse = await api.get(`/tests/${testId}/conditional-structure`)
    const currentStructure = structureResponse.data.ConditionalStructure || {}

    console.log('📊 Structure actuelle:', {
      existingNodes: currentStructure.nodes?.length || 0,
      existingEdges: currentStructure.edges?.length || 0,
    })

    // 5) Merger avec la structure actuelle
    const mergedStructure = {
      nodes: [...(currentStructure.nodes || []), newNode],
      edges: [...(currentStructure.edges || [])],
      startNode: currentStructure.startNode || null,
      terminationNodes: [...(currentStructure.terminationNodes || [])],
      viewport: currentStructure.viewport || { x: 0, y: 0, zoom: 1 },
    }

    console.log('📊 Structure mergée:', {
      totalNodes: mergedStructure.nodes.length,
      totalEdges: mergedStructure.edges.length,
    })

    // 6) Sauvegarder via PUT (replace:true pour garantir cohérence)
    console.log('📦 [handleItemBankImport] Sauvegarde structure via PUT')
    await api.put(`/tests/${testId}/conditional-structure`, {
      ...mergedStructure,
      replace: true,
    })

    console.log('✅ [handleItemBankImport] Structure sauvegardée')

    // 7) Fermer la modale
    showItemBank.value = false

    // 8) Recharger la structure pour affichage
    console.log('📦 [handleItemBankImport] Rechargement structure pour affichage')
    await loadStructure()

    // 9) Centrer sur le nouveau node
    setTimeout(() => {
      fitView({ padding: 0.2, duration: 200 })
    }, 100)

    console.log('✅ [handleItemBankImport] Import terminé avec succès')
  } catch (err) {
    console.error('❌ [handleItemBankImport] Erreur:', err?.response?.data || err)
    alert('Erreur lors de l\'import depuis la bibliothèque d\'items')
  }
}

const activeEditor = ref(null) // { type: 'choice' | 'choiceBlock' | 'message' | 'order' | 'shortAnswer' | 'textGaps' | 'textPuzzle' | 'makingPairs' | 'match', nodeId, elementId }

// Debug: Vérifier les imports
console.log('🔍 ChoiceBlockEditor:', ChoiceBlockEditor)
console.log('🔍 ChoiceBlockPreview:', ChoiceBlockPreview)

const editorRegistry = {
  choice: { editor: ChoiceEditor, preview: ChoicePreview },
  choiceBlock: { editor: ChoiceBlockEditor, preview: ChoiceBlockPreview },
  message: { editor: MessageEditor, preview: MessagePreview },
  order: { editor: OrderEditor, preview: OrderPreview },
  shortAnswer: { editor: ShortAnswerEditor, preview: ShortAnswerPreview },
  textGaps: { editor: TextGapsEditor, preview: TextGapsPreview },
  textPuzzle: { editor: TextPuzzleEditor, preview: TextPuzzlePreview },
  makingPairs: { editor: MakingPairsEditor, preview: MakingPairsPreview },
  match: { editor: MatchEditor, preview: MatchPreview },
  hotSpot: { editor: HotSpotEditor, preview: HotSpotPreview },
}

// -------------------- Store --------------------
const testStore = useTestStore()
const { ConditionalStructure } = storeToRefs(testStore)

// -------------------- Helpers --------------------
// Fonction pour obtenir le centre du viewport actuel
function getCenterPosition() {
  const vp = getViewport()
  const flowContainer = document.querySelector('.flow-container')
  if (!flowContainer) {
    return { x: 400, y: 300 } // Fallback si le conteneur n'est pas trouvé
  }

  const rect = flowContainer.getBoundingClientRect()
  const centerX = (rect.width / 2 - vp.x) / vp.zoom
  const centerY = (rect.height / 2 - vp.y) / vp.zoom

  return { x: centerX, y: centerY }
}

// -------------------- Elements helpers (double écriture) --------------------
function ensureElementsArray() {
  if (!Array.isArray(props.testData.elements)) props.testData.elements = []
  return props.testData.elements
}

// function makeElement(el_Type, label = 'Untitled') {
//   return {
//     el_ID: uuidv4(),
//     el_Type,
//     el_Text: label || 'Untitled',
//     difficulty_rating: 5,
//     options: el_Type === 'choice' ? [] : undefined,
//     scoring: {},
//     metadata: { placeholder: true, createdAt: new Date().toISOString() },
//   }
// }

function upsertElement(el) {
  const list = ensureElementsArray()
  const idx = list.findIndex(e => e.el_ID === el.el_ID)
  if (idx === -1) props.testData.elements = [...list, el]
  else props.testData.elements = [
    ...list.slice(0, idx),
    { ...list[idx], ...el },
    ...list.slice(idx + 1),
  ]
  try { testStore.updateElement(el) } catch (_) { }
  return el
}

function getElementById(id) {
  const allElements = ensureElementsArray()
  console.log('🔍 [getElementById] Searching for id:', id)
  console.log('  📚 Available elements:', allElements.map(e => ({ el_ID: e.el_ID, el_Text: e.el_Text })))

  const found = allElements.find(e => e.el_ID === id) || null

  if (found) {
    console.log('  ✅ Found element:', { el_ID: found.el_ID, el_Text: found.el_Text })
  } else {
    console.log('  ❌ Element NOT FOUND for id:', id)
  }

  return found
}

// -------------------- Mapping "choice → question" pour l'API --------------------
const API_NODE_TYPE = new Set(['start', 'question', 'termination', 'switcher', 'page'])
const toPersistType = t => (API_NODE_TYPE.has(t) ? t : 'question')  // ex: 'choice' → 'question'


//-----------------NODE tools for graphs------------------

function createStartNode() {
  if (hasStartNode.value) {
    console.warn('Un nœud de départ existe déjà.')
    return
  }

  const node = {
    id: uuidv4(),
    type: 'start',
    position: { x: 80, y: 100 },
    style: { width: 200, height: 80, background: '#e6ffe6' },
    data: { label: 'Start' },
  }

  nodes.value = [...nodes.value, node]
  requestSaveStructure()
  console.log('✅ StartNode créé et sauvegardé.')
}

function createTerminationNode() {
  const node = {
    id: uuidv4(),
    type: 'termination',
    position: { x: 600, y: 100 },
    style: { width: 200, height: 80, background: '#fff5e6', border: '2px solid #ffc107' },
    data: { label: 'The end' },
  }

  nodes.value = [...nodes.value, node]
  requestSaveStructure()
  console.log('✅ TerminationNode créé et sauvegardé.')
}

function createSwitcherNode() {
  const node = {
    id: uuidv4(),
    type: 'switcher',
    position: { x: 350, y: 200 },
    style: { width: 240, height: 100, background: '#e6f0ff', border: '2px solid #0d6efd' },
    data: {
      label: 'Switcher',
      conditions: [], // ✅ Tableau vide pour stocker les conditions de comparaison
      collapsed: false
    },
  }

  nodes.value = [...nodes.value, node]
  requestSaveStructure()
  console.log('✅ SwitcherNode créé avec conditions=[]')
}



// -------------------- Modale de création --------------------
function addNode(type) {
  pendingType.value = type
  modalKey.value++
  showCreateModal.value = true

  console.log("PASSAGE OBLIGE")

}

function addPage() {
  console.log("📘 Creating nested PageNode…")
  const pageId = uuidv4()

  const pageNode = {
    id: pageId,
    type: 'page',
    position: getCenterPosition(),
    style: {
      width: 600,
      height: 400,
      background: '#f0f8ff',
      border: '2px dashed #4dabf7'
    },
    data: {
      label: 'Nouvelle page',
      description: '',
      collapsed: false,
      navigationMode: 'all-required',
      mode: 'score'
    }
  }

  nodes.value = [...nodes.value, pageNode]

  // Ouvrir l'éditeur pour cette page
  selectedPageForEdit.value = pageNode
  showPageEditor.value = true

  console.log('✅ PageNode créé, ouverture de l\'éditeur')
}

const selectedPageId = ref(null)
const selectedPageForEdit = ref(null)
const showPageEditor = ref(false)


function addChildToSelectedPage() {
  if (!selectedPageId.value) return alert('Aucune page sélectionnée')
  const childId = uuidv4()
  nodes.value.push({
    id: childId,
    type: 'choice',
    parentNode: selectedPageId.value,
    extent: 'parent',
    position: { x: 80, y: 80 },
    data: { label: 'Question dans la page' },
    style: { width: 260, height: 120 }
  })
}

// Handlers pour PageEditor
function onPageEditorSave(payload) {
  console.log('💾 Sauvegarde de la page:', payload)
  nodes.value = payload.nodes
  showPageEditor.value = false
  selectedPageForEdit.value = null
  requestSaveStructure()
}

function onPageEditorCancel() {
  console.log('❌ Annulation de l\'édition de page')
  showPageEditor.value = false
  selectedPageForEdit.value = null
}

function onPageNodesUpdate(updatedNodes) {
  nodes.value = updatedNodes
}

// Compter les enfants d'une page
function getChildrenCount(pageId) {
  return nodes.value.filter(n => n.parentNode === pageId).length
}

// Ouvrir l'éditeur pour une page existante
function openPageEditor({ nodeId }) {
  const pageNode = nodes.value.find(n => n.id === nodeId)
  if (pageNode) {
    selectedPageForEdit.value = pageNode
    showPageEditor.value = true
  }
}

// Supprimer une page et tous ses enfants
function deletePageNode({ nodeId }) {
  if (!confirm('⚠️ Supprimer cette page et tous ses items ?')) return

  // Supprimer les enfants
  const childrenIds = nodes.value
    .filter(n => n.parentNode === nodeId)
    .map(n => n.id)

  // Supprimer les éléments correspondants
  childrenIds.forEach(childId => {
    const node = nodes.value.find(n => n.id === childId)
    const elementId = node?.data?.elementId
    if (elementId && props.testData.elements) {
      const elementIndex = props.testData.elements.findIndex(el => el.el_ID === elementId)
      if (elementIndex !== -1) {
        props.testData.elements.splice(elementIndex, 1)
      }
    }
  })

  // Supprimer tous les nodes (page + enfants)
  nodes.value = nodes.value.filter(n => n.id !== nodeId && !childrenIds.includes(n.id))

  // Supprimer les edges liés
  edges.value = edges.value.filter(e => e.source !== nodeId && e.target !== nodeId)

  requestSaveStructure()
  console.log('✅ Page et ses enfants supprimés')
}




// Parent: ConditionalTestEditor.vue
const isCreatingNode = ref(false)
const showDisplaySettings = ref(false)
const displaySettings = ref({
  containerMargin: 20,
  containerPadding: 20,
  questionSpacing: 15,
  pagePadding: 15,
  containerBackground: '#ffffff',
  pageBackground: '#f8f9fa',
  maxWidth: 1200,
  containerAlignment: 'center'
})

async function onCreateConfirm(payload) {
  showCreateModal.value = false
  pendingType.value = null

  if (!payload) {
    console.warn('[onCreateConfirm] Payload vide, annulation.')
    return
  }

  // Cas spécial : création par IA (plusieurs éléments/nodes)
  if (payload.created && payload.nodes && payload.elements) {
    console.log('🤖 Confirmation IA - Ajout de', payload.nodes.length, 'nodes');

    // ⚠️ NE PAS ajouter manuellement les nodes ici !
    // Ils ont déjà été sauvegardés en base par AIGeneratorWizard
    // On doit recharger depuis le serveur pour éviter les conflits

    // Rafraîchir l'affichage en rechargeant la structure
    await loadStructure();

    // Attendre le rendu et forcer l'affichage
    await nextTick();

    // Forcer VueFlow à se mettre à jour
    setTimeout(() => {
      fitView({ padding: 0.2, duration: 200 });
    }, 100);

    console.log('✅ Structure rechargée avec', payload.nodes.length, 'nouveaux nodes IA');
    console.log('📊 Nodes actuellement dans le graphe:', nodes.value.length);
    return;
  }

  const { el_Type, elementId, label, node, persisted } = payload

  // 1️⃣ Assure-toi que l'élément existe dans testData.elements
  if (elementId) {
    const existing = getElementById(elementId)
    if (!existing) {
      console.log(`🆕 Élément ${elementId} ajouté dans testData.elements`)

      // ✅ Structure différente selon le type
      const newElement = {
        el_ID: elementId,
        el_Type: el_Type ?? 'choice',
        el_Text: label ?? '',
        el_RichText: '',
        options: [],
        feedback: { correct: '', incorrect: '' },
        multiple: false,
        randomized: false,
        metadata: { createdAt: new Date().toISOString() }
      }

      // ✅ Pour choiceBlock : ajouter maxScore et tip
      if (el_Type === 'choiceBlock') {
        newElement.maxScore = 1
        newElement.tip = ''
      }

      // ✅ Pour message : pas d'options, juste le richText
      if (el_Type === 'message') {
        delete newElement.options
        delete newElement.feedback
        delete newElement.multiple
        delete newElement.randomized
      }

      // ✅ Pour order : labels array et maxScore
      if (el_Type === 'order') {
        delete newElement.options
        delete newElement.multiple
        delete newElement.randomized
        newElement.labels = []
        newElement.maxScore = 1
        newElement.feedback = { correct: '', incorrect: '' }
      }

      // ✅ Pour shortAnswer : propriétés pour la correction
      if (el_Type === 'shortAnswer') {
        delete newElement.options
        delete newElement.multiple
        delete newElement.randomized
        newElement.maxScore = 1
        newElement.useGrid = false
        newElement.correctionAxes = []
        newElement.hasTextReference = false
        newElement.refText = ''
        newElement.AIisActivated = false
      }

      // ✅ Pour textGaps : gaps array et el_GapsedText
      if (el_Type === 'textGaps') {
        delete newElement.options
        delete newElement.multiple
        delete newElement.randomized
        newElement.gaps = []
        newElement.el_GapsedText = ''
      }

      // ✅ Pour textPuzzle : el_InteractionData avec gaps et sourceText
      if (el_Type === 'textPuzzle') {
        delete newElement.options
        delete newElement.multiple
        delete newElement.randomized
        newElement.el_InteractionData = {
          gaps: [],
          sourceText: ''
        }
      }

      // ✅ Pour makingPairs : el_InteractionData avec expectedPairs et pointsPerPair
      if (el_Type === 'makingPairs') {
        delete newElement.options
        delete newElement.multiple
        delete newElement.randomized
        newElement.el_InteractionData = {
          expectedPairs: [],
          pointsPerPair: 1
        }
        newElement.maxScore = 0
      }

      // ✅ Pour match : el_Data avec rows, columns, solutionMatrix et scoring
      if (el_Type === 'match') {
        delete newElement.options
        delete newElement.multiple
        delete newElement.randomized
        newElement.el_Data = {
          rows: ['Choix #1', 'Choix #2'],
          columns: ['Option #1', 'Option #2'],
          solutionMatrix: [[false, false], [false, false]],
          scoring: {
            right: 1,
            wrong: 0
          }
        }
      }

      // ✅ Pour hotSpot : el_Data (zones array) et el_data (penalties)
      if (el_Type === 'hotSpot') {
        delete newElement.options
        delete newElement.multiple
        delete newElement.randomized
        newElement.el_Data = [] // Array of zones
        newElement.el_data = {
          penalties: 0
        }
        newElement.maxScore = 0
      }

      props.testData.elements.push(newElement)
    }
  }

  // 2️⃣ Si CreateComponent ne fournit pas encore de node → on le crée ici
  let newNode = node
  if (!newNode) {
    // ✅ Détermine le type de node selon el_Type
    let nodeType = 'question'
    if (el_Type === 'choice') nodeType = 'choice'
    if (el_Type === 'choiceBlock') nodeType = 'choiceBlock'
    if (el_Type === 'message') nodeType = 'message'
    if (el_Type === 'order') nodeType = 'order'
    if (el_Type === 'shortAnswer') nodeType = 'shortAnswer'
    if (el_Type === 'textGaps') nodeType = 'textGaps'
    if (el_Type === 'textPuzzle') nodeType = 'textPuzzle'
    if (el_Type === 'makingPairs') nodeType = 'makingPairs'
    if (el_Type === 'match') nodeType = 'match'
    if (el_Type === 'hotSpot') nodeType = 'hotSpot'

    // ✅ Déterminer le mode par défaut selon le type
    let defaultMode
    if (['order', 'makingPairs', 'textGaps', 'textPuzzle', 'hotSpot', 'match'].includes(nodeType)) {
      defaultMode = 'score' // Interactions de scoring automatique
    } else if (['message', 'shortAnswer'].includes(nodeType)) {
      defaultMode = 'options' // Interactions d'orientation uniquement
    } else if (['choice', 'choiceBlock'].includes(nodeType)) {
      defaultMode = 'options' // Par défaut options, mais peut être changé en score
    } else {
      defaultMode = undefined // start, termination, switcher n'ont pas de mode
    }

    newNode = {
      id: uuidv4(),
      type: nodeType,
      position: getCenterPosition(),
      data: {
        elementId: elementId,
        label: label ?? 'Nouvelle question',
        mode: defaultMode,
        collapsed: false,
        subtype: (nodeType === 'choice' || nodeType === 'choiceBlock' || nodeType === 'message' || nodeType === 'order' || nodeType === 'shortAnswer' || nodeType === 'textGaps' || nodeType === 'textPuzzle' || nodeType === 'makingPairs' || nodeType === 'match' || nodeType === 'hotSpot') ? nodeType : null
      },
      style: { width: 280, height: 140 }
    }
    console.log(`🆕 Node ${nodeType} créé avec mode="${defaultMode}"`)
  } else {
    // si le node vient de CreateComponent, on lui injecte aussi l'elementId
    // ✅ Déterminer le mode par défaut selon le type
    let defaultMode
    if (['order', 'makingPairs', 'textGaps', 'textPuzzle', 'hotSpot', 'match'].includes(el_Type)) {
      defaultMode = 'score'
    } else if (['message', 'shortAnswer'].includes(el_Type)) {
      defaultMode = 'options'
    } else if (['choice', 'choiceBlock'].includes(el_Type)) {
      defaultMode = newNode.data?.mode ?? 'options' // Garde le mode existant ou options par défaut
    } else {
      defaultMode = undefined
    }

    newNode.data = {
      ...(newNode.data || {}),
      elementId,
      label: label ?? newNode.data?.label ?? 'Nouvelle question',
      mode: defaultMode,
      subtype: (el_Type === 'choice' || el_Type === 'choiceBlock' || el_Type === 'message' || el_Type === 'order' || el_Type === 'shortAnswer' || el_Type === 'textGaps' || el_Type === 'textPuzzle' || el_Type === 'makingPairs' || el_Type === 'match' || el_Type === 'hotSpot') ? el_Type : null
    }
  }

  // 3️⃣ Ajoute le node au flow
  nodes.value = [...nodes.value, newNode]

  // 4️⃣ Sauvegarde structure et mapping
  requestSaveStructure()

  console.log(`✅ Node ${newNode.id} lié à element ${elementId} ajouté au graphe`)
}



async function flushSaveStructure() {
  // annule le debounce courant
  clearTimeout(saveTimer)
  // s'il y a déjà une save en vol, on attend proprement
  if (isSavingStructure.value) {
    await saveInFlight.catch(() => { })
  }
  // lance la save immédiatement (pas via debounce)
  await saveStructure()
}




function onCreateCancel() {
  showCreateModal.value = false
  pendingType.value = null
}

// -------------------- Création de nœud (double écriture ici) --------------------
function createNode({ type, elementId = null, label }) {
  // Pour question/choice, elementId doit exister (garanti par onCreateConfirm)
  if ((type === 'question' || type === 'choice') && !elementId) {
    console.warn('[createNode] elementId requis pour', type)

    return
  }

  const node = {
    id: uuidv4(),
    type,
    position: getCenterPosition(),
    collapsed: false,
    style: { width: 280, height: 140 },
    data: {
      elementId,
      label: label ?? (type === 'start' ? 'Start' : type === 'termination' ? 'Fin' : 'Nouvelle question'),
      mode: type === 'question' ? 'options' : undefined,
    },
  }

  nodes.value = [...nodes.value, node]

  // pas de fitView agressif ici (il peut “manger” des rafales)
  requestSaveStructure()
  return node.id
}


// -------------------- Éditeur bas de page --------------------

// Helper function to ensure element exists in store (reload if needed)
async function ensureElementLoaded(elementId, editorType) {
  console.log(`🔍 [${editorType}] Ensuring element loaded:`, elementId)

  let element = getElementById(elementId)

  // Si l'élément n'est pas trouvé, recharger depuis la base de données
  if (!element && elementId) {
    console.log(`  🔄 [${editorType}] Element not found in store, reloading from database...`)
    const testId = props.testData._id || props.testData.ID
    try {
      const { data } = await api.get(`/tests/${testId}`)

      // L'API retourne { test: {...} }, pas directement les elements
      const elements = data?.test?.elements || []

      if (elements.length > 0) {
        console.log(`  📥 [${editorType}] Received elements from API:`, elements.length)
        testStore.testData.elements = elements

        // Réessayer de trouver l'élément
        element = getElementById(elementId)
        if (element) {
          console.log(`  ✅ [${editorType}] Element found after reload:`, element.el_Text)
        } else {
          console.error(`  ❌ [${editorType}] Element STILL NOT FOUND after reload:`, elementId)
          console.error(`  📋 [${editorType}] Available element IDs:`, elements.map(e => e.el_ID))
        }
      } else {
        console.error(`  ❌ [${editorType}] No elements in API response`)
      }
    } catch (error) {
      console.error(`  ❌ [${editorType}] Failed to reload elements:`, error)
    }
  }

  if (element) {
    testStore.selectedElement = element
    testStore.selectedElementLabel = element.el_Text || `Element ${elementId}`
    console.log(`  ✅ [${editorType}] testStore.selectedElement set to:`, testStore.selectedElement.el_Text)
    return true
  } else {
    console.log(`  ❌ [${editorType}] NO ELEMENT FOUND for elementId:`, elementId)
    alert('⚠️ Erreur: L\'élément n\'existe pas dans la base de données.\n\nIl semble que cet élément ait été créé mais pas sauvegardé correctement.\n\nVeuillez supprimer ce node et le recréer.')
    return false
  }
}

async function openChoiceEditor({ nodeId, elementId }) {
  const success = await ensureElementLoaded(elementId, 'ChoiceEditor')
  if (!success) return
  activeEditor.value = { type: 'choice', nodeId, elementId }
}

async function openChoiceBlockEditor({ nodeId, elementId }) {
  const success = await ensureElementLoaded(elementId, 'ChoiceBlockEditor')
  if (!success) return
  activeEditor.value = { type: 'choiceBlock', nodeId, elementId }
}

async function openMessageEditor({ nodeId, elementId }) {
  const success = await ensureElementLoaded(elementId, 'MessageEditor')
  if (!success) return
  activeEditor.value = { type: 'message', nodeId, elementId }
}

async function openOrderEditor({ nodeId, elementId }) {
  const success = await ensureElementLoaded(elementId, 'OrderEditor')
  if (!success) return
  activeEditor.value = { type: 'order', nodeId, elementId }
}

async function openShortAnswerEditor({ nodeId, elementId }) {
  const success = await ensureElementLoaded(elementId, 'ShortAnswerEditor')
  if (!success) return
  activeEditor.value = { type: 'shortAnswer', nodeId, elementId }
}

async function openTextGapsEditor({ nodeId, elementId }) {
  const success = await ensureElementLoaded(elementId, 'TextGapsEditor')
  if (!success) return
  activeEditor.value = { type: 'textGaps', nodeId, elementId }
}

async function openTextPuzzleEditor({ nodeId, elementId }) {
  const success = await ensureElementLoaded(elementId, 'TextPuzzleEditor')
  if (!success) return
  activeEditor.value = { type: 'textPuzzle', nodeId, elementId }
}

async function openMakingPairsEditor({ nodeId, elementId }) {
  const success = await ensureElementLoaded(elementId, 'MakingPairsEditor')
  if (!success) return
  activeEditor.value = { type: 'makingPairs', nodeId, elementId }
}

async function openMatchEditor({ nodeId, elementId }) {
  const success = await ensureElementLoaded(elementId, 'MatchEditor')
  if (!success) return
  activeEditor.value = { type: 'match', nodeId, elementId }
}

async function openHotSpotEditor({ nodeId, elementId }) {
  const success = await ensureElementLoaded(elementId, 'HotSpotEditor')
  if (!success) return
  activeEditor.value = { type: 'hotSpot', nodeId, elementId }
}

function closeEditor() { activeEditor.value = null }

function onEditorUpdated({ elementId, patch }) {
  const node = nodes.value.find(n => n.id === activeEditor.value?.nodeId)
  if (!node) return

  node.data = { ...node.data, ...patch, elementId: elementId ?? node.data.elementId }
  nodes.value = [...nodes.value]

  // upsert côté éléments si patch touche le contenu
  if (node.data.elementId) {
    const existing = getElementById(node.data.elementId)
    if (existing) upsertElement({ ...existing, ...patch })
    requestSaveStructure()
  }
}

// ---------- Debounce infra ----------
let saveTimer = null
const SAVE_DEBOUNCE_MS = 400
let saveInFlight = Promise.resolve()

function requestSaveStructure() {
  clearTimeout(saveTimer)
  saveTimer = setTimeout(() => { void saveStructure() }, SAVE_DEBOUNCE_MS)
}

// ---------- Nouvelle saveStructure robuste ----------
/**
 * Détermine le mode par défaut selon le type de node
 * @param {string} nodeType - Le type du node (subtype ou type)
 * @returns {string|undefined} - "score", "options", ou undefined
 */
function getDefaultModeForNodeType(nodeType) {
  // Nodes en mode "score" : comptent les points
  if (['order', 'makingPairs', 'textGaps', 'textPuzzle', 'hotSpot', 'match'].includes(nodeType)) {
    return 'score';
  }
  // Nodes en mode "options" : navigation sans score
  if (['message', 'shortAnswer', 'choice', 'choiceBlock'].includes(nodeType)) {
    return 'options';
  }
  // Autres nodes (start, termination, switcher) : pas de mode
  return undefined;
}

async function saveStructure() {
  const testId = props.testData._id || props.testData.ID
  console.log('🔵 saveStructure appelée, testId:', testId)
  if (!testId) {
    console.warn('⚠️ Pas de testId (ni _id ni ID), sauvegarde annulée')
    return
  }
  if (isSavingStructure.value) {
    console.log('🟡 Sauvegarde déjà en cours, attente...')
    // Une sauvegarde est déjà en vol : on chaîne proprement
    await saveInFlight.catch(() => { })
  }

  // 1️⃣ Vérification préliminaire
  for (const n of nodes.value) {
    if (toPersistType(n.type) === 'question' && !n.data?.elementId) {
      console.warn('[saveStructure] node sans elementId (ignorer/retourner si nécessaire):', n.id)
    }
  }

  // 2️⃣ Préparation des nodes à persister
  // 2️⃣ Préparation des nodes à persister (inclut containers)
  const payloadNodes = nodes.value.map(n => {
    const effectiveType = n.data?.subtype ?? n.type

    // ⚙️ Déterminer le mode selon le type
    let nodeMode = n.data?.mode ?? getDefaultModeForNodeType(effectiveType)

    const baseData = {
      elementId: n.data?.elementId ?? null,
      label: n.data?.label ?? null,
      mode: nodeMode,
      subtype: effectiveType,
      // ✅ Pour les conteneurs
      description: n.data?.description ?? null,
      navigationMode: n.data?.navigationMode ?? null,
      collapsed: !!n.data?.collapsed,
    }

    // ✅ Switchers : conserver conditions
    if (n.type === 'switcher') {
      baseData.conditions = n.data?.conditions ?? []
    }

    // ✅ Termination nodes : conserver endBonus
    if (n.type === 'termination') {
      baseData.endBonus = n.data?.endBonus ?? 0
      console.log('📍 [saveStructure] Termination node:', {
        nodeId: n.id,
        endBonusFromData: n.data?.endBonus,
        fullNodeData: n.data
      })
    }

    // ✅ Pages / containers : enfants explicites
    if (n.type === 'page') {
      baseData.children = nodes.value
        .filter(child => child.parentNode === n.id)
        .map(child => child.id)
    }

    return {
      id: n.id,
      type: n.type, // conserve le vrai type ("page", "choice", etc.)
      parentNode: n.parentNode ?? null,
      position: { x: n.position.x, y: n.position.y },
      extent: n.extent ?? undefined,
      style: n.style ?? {},
      data: baseData,
    }
  })


  console.log('📦 Nodes à sauvegarder:', payloadNodes.length, 'dont textPuzzle:', payloadNodes.filter(n => n.data.subtype === 'textPuzzle').length)

  // 3️⃣ Préparation des edges
  const payloadEdges = edges.value.map(e => ({
    id: e.id,
    type: e.type || 'default',
    source: e.source,
    target: e.target,
    sourceHandle: e.sourceHandle ?? null,
    targetHandle: e.targetHandle ?? null,
    label: e.label ?? null,
    data: e.data ? {
      condition: e.data.condition ? {
        type: e.data.condition.type ?? 'always',
        operator: e.data.condition.operator ?? '==',
        value: e.data.condition.value ?? null,
        label: e.data.condition.label ?? null,
      } : undefined
    } : undefined
  }))

  const vp = getViewport()
  const startId = nodes.value.find(n => n.type === 'start')?.id || null
  const termIds = nodes.value.filter(n => n.type === 'termination').map(n => n.id)

  // 4️⃣ NOUVEAU : On sauvegarde TOUS les nodes, connectés ou non
  // Plus besoin de supprimer les éléments orphelins automatiquement
  const elementIdsToDelete = []

  // 5️⃣ Verrouillage
  isSaving.value = true
  isSavingStructure.value = true

  // 6️⃣ Sauvegarde unique côté serveur
  const payload = {
    nodes: payloadNodes.map(n => ({
      id: n.id,
      type: n.type,
      parentNode: n.parentNode ?? null,
      position: n.position,
      extent: n.extent,
      data: n.data,
    })),
    edges: payloadEdges,
    startNode: startId,
    terminationNodes: termIds,
    viewport: vp,
    displaySettings: displaySettings.value,
    replace: true
  }

  console.log('📤 [saveStructure] Envoi payload:', {
    nodesCount: payload.nodes.length,
    edgesCount: payload.edges.length,
    startNode: payload.startNode,
    terminationNodes: payload.terminationNodes,
    displaySettings: payload.displaySettings
  })

  // Log spécifique pour les termination nodes
  const termNodes = payload.nodes.filter(n => n.type === 'termination')
  termNodes.forEach(tn => {
    console.log('🏁 [Frontend] Termination node envoyé:', {
      id: tn.id,
      label: tn.data?.label,
      endBonus: tn.data?.endBonus,
      fullData: tn.data
    })
  })

  saveInFlight = api.put(`/tests/${testId}/conditional-structure`, payload)

  try {
    const response = await saveInFlight
    console.log('✅ [saveStructure] Structure sauvegardée avec succès, réponse:', response.data)
  } catch (err) {
    console.error('❌ [saveStructure] Erreur sauvegarde:', err)
    console.error('❌ [saveStructure] Détails:', err.response?.data || err.message)
  } finally {
    isSavingStructure.value = false
    isSaving.value = false
  }
}




// -------------------- Chargement (normalisation + re-type) --------------------
function normalizeLoadedNodes(raw = []) {
  return raw.map(n => {
    const shouldBeChoice =
      (n?.type === 'question' && n?.data?.subtype === 'choice') ||
      n?.type === 'choice'

    const shouldBeChoiceBlock =
      (n?.type === 'question' && n?.data?.subtype === 'choiceBlock') ||
      n?.type === 'choiceBlock'

    const shouldBeMessage =
      (n?.type === 'question' && n?.data?.subtype === 'message') ||
      n?.type === 'message'

    const shouldBeOrder =
      (n?.type === 'question' && n?.data?.subtype === 'order') ||
      n?.type === 'order'

    const shouldBeShortAnswer =
      (n?.type === 'question' && n?.data?.subtype === 'shortAnswer') ||
      n?.type === 'shortAnswer'

    const shouldBeTextGaps =
      (n?.type === 'question' && n?.data?.subtype === 'textGaps') ||
      n?.type === 'textGaps'

    const shouldBeTextPuzzle =
      (n?.type === 'question' && n?.data?.subtype === 'textPuzzle') ||
      n?.type === 'textPuzzle'

    const shouldBeMakingPairs =
      (n?.type === 'question' && n?.data?.subtype === 'makingPairs') ||
      n?.type === 'makingPairs'

    const shouldBeMatch =
      (n?.type === 'question' && n?.data?.subtype === 'match') ||
      n?.type === 'match'

    const shouldBeHotSpot =
      (n?.type === 'question' && n?.data?.subtype === 'hotSpot') ||
      n?.type === 'hotSpot'

    // 🔹 Détermination du type final du node
    let nodeType = n.type
    if (shouldBeChoice) nodeType = 'choice'
    if (shouldBeChoiceBlock) nodeType = 'choiceBlock'
    if (shouldBeMessage) nodeType = 'message'
    if (shouldBeOrder) nodeType = 'order'
    if (shouldBeShortAnswer) nodeType = 'shortAnswer'
    if (shouldBeTextGaps) nodeType = 'textGaps'
    if (shouldBeTextPuzzle) nodeType = 'textPuzzle'
    if (shouldBeMakingPairs) nodeType = 'makingPairs'
    if (shouldBeMatch) nodeType = 'match'
    if (shouldBeHotSpot) nodeType = 'hotSpot'

    // ✅ Base commune
    const baseData = {
      elementId: n.data?.elementId ?? null,
      label: n.data?.label ?? null,
      collapsed: !!n.collapsed
    }

    // ✅ Pour les switchers : conditions
    if (nodeType === 'switcher') {
      baseData.conditions = n.data?.conditions ?? []
    }

    // ✅ Pour les questions / choice / choiceBlock : mode & subtype
    if (nodeType === 'choice' || nodeType === 'choiceBlock' || nodeType === 'question') {
      baseData.mode = n.data?.mode ?? 'options'
      baseData.subtype = n.data?.subtype ?? (
        shouldBeChoice ? 'choice'
          : shouldBeChoiceBlock ? 'choiceBlock'
            : null
      )
    }

    // ✅ Pour les interactions simples
    const simpleSubtypes = ['message', 'order', 'shortAnswer', 'textGaps', 'textPuzzle', 'makingPairs', 'match', 'hotSpot']
    if (simpleSubtypes.includes(nodeType)) {
      baseData.subtype = nodeType
    }

    // ✅ 🔥 NOUVEAU : pour les conteneurs de type "page"
    if (nodeType === 'page') {
      baseData.description = n.data?.description ?? ''
      baseData.navigationMode = n.data?.navigationMode ?? 'all-required'
      baseData.collapsed = !!n.data?.collapsed
      baseData.children = n.data?.children ?? []  // liste d'IDs des nœuds internes
      baseData.mode = n.data?.mode ?? 'score'
    }

    // ✅ Pour les termination nodes : endBonus
    if (nodeType === 'termination') {
      baseData.endBonus = n.data?.endBonus ?? 0
    }

    return {
      id: n.id,
      type: nodeType,
      parentNode: n.parentNode ?? null,   // 🔹 important pour les nested nodes
      extent: n.extent ?? undefined,      // 🔹 limite de déplacement
      position: n.position || { x: 120, y: 120 },
      collapsed: !!n.collapsed,
      style: n.style || { width: 280, height: 140 },
      data: baseData
    }
  })
}

async function loadStructure() {
  const testId = props.testData._id || props.testData.ID
  if (!testId) {
    console.warn('⚠️ [loadStructure] Pas de testId (ni _id ni ID)')
    return
  }

  // ⚠️ NE PAS charger si une opération est déjà en cours
  if (isLoadingStructure.value || isSavingStructure.value) {
    console.log('⚠️ [loadStructure] BLOQUÉ - opération en cours')
    return
  }

  console.log('🔄 [loadStructure] Chargement pour testId:', testId)
  console.trace('🔄 [loadStructure] Call stack:')
  isLoadingStructure.value = true
  try {
    const { data } = await api.get(`/tests/${testId}/conditional-structure`)
    console.log('🔍 [loadStructure] Réponse API complète:', data)

    const structure = data?.ConditionalStructure || {}
    console.log('🔍 [loadStructure] ConditionalStructure extraite:', structure)

    // ✅ Mettre à jour les éléments du test (inclut les nouveaux éléments IA)
    if (data?.elements) {
      console.log('📦 Mise à jour de testData.elements:', data.elements.length, 'éléments');
      console.log('📦 IDs des éléments:', data.elements.map(el => el.el_ID));
      testStore.testData.elements = data.elements;
    } else {
      console.warn('⚠️ Aucun élément retourné par l\'API');
    }

    // ✅ Load display settings if available
    if (data?.displaySettings) {
      displaySettings.value = { ...displaySettings.value, ...data.displaySettings };
      props.testData.displaySettings = data.displaySettings;
      console.log('🎨 [loadStructure] Display settings loaded from API:', data.displaySettings);
      console.log('🎨 [loadStructure] displaySettings.value:', displaySettings.value);
      console.log('🎨 [loadStructure] props.testData.displaySettings:', props.testData.displaySettings);
    } else {
      console.warn('⚠️ [loadStructure] No displaySettings in API response');
    }

    console.log('📥 Structure chargée depuis le backend:', {
      totalNodes: structure.nodes?.length || 0,
      totalEdges: structure.edges?.length || 0,
      totalElements: data?.elements?.length || 0,
      textPuzzleNodes: structure.nodes?.filter(n => n.data?.subtype === 'textPuzzle' || n.type === 'textPuzzle').length || 0,
      hotSpotNodes: structure.nodes?.filter(n => n.data?.subtype === 'hotSpot' || n.type === 'hotSpot').length || 0,
      rawNodes: structure.nodes,
      nestedNodes: structure.nodes?.filter(n => n.parentNode).map(n => ({ id: n.id, parentNode: n.parentNode}))
    })

    const normalizedNodes = normalizeLoadedNodes(structure.nodes || [])

    console.log('🗑️ [loadStructure] AVANT filtrage:', {
      normalizedCount: normalizedNodes.length,
      deletedNodeIds: deletedNodeIds.value,
      nodeIds: normalizedNodes.map(n => n.id)
    })

    // 🗑️ Filtrer les nodes supprimés
    nodes.value = normalizedNodes.filter(n => {
      const isDeleted = deletedNodeIds.value.includes(n.id)
      if (isDeleted) {
        console.log(`🗑️ [loadStructure] Filtrage du node ${n.id}`)
      }
      return !isDeleted
    })

    console.log('🗑️ [loadStructure] APRÈS filtrage:', {
      resultCount: nodes.value.length,
      filtered: normalizedNodes.length - nodes.value.length
    })

    if (normalizedNodes.length !== nodes.value.length) {
      console.log(`🗑️ [loadStructure] ${normalizedNodes.length - nodes.value.length} node(s) supprimé(s) filtré(s)`, deletedNodeIds.value)
    }

    console.log('📊 Nodes après normalisation:', {
      totalNodes: nodes.value.length,
      textPuzzleNodes: nodes.value.filter(n => n.type === 'textPuzzle').length,
      hotSpotNodes: nodes.value.filter(n => n.type === 'hotSpot').length,
      nodeTypes: [...new Set(nodes.value.map(n => n.type))],
      nodesWithElementId: nodes.value.filter(n => n.data?.elementId).length,
      sampleNodeData: nodes.value[0]?.data
    })

    // 🗑️ Filtrer les edges liés aux nodes supprimés
    const nodeIds = new Set(nodes.value.map(n => n.id))
    edges.value = (structure.edges || []).filter(e =>
      nodeIds.has(e.source) && nodeIds.has(e.target)
    )
    console.log('🔗 Edges chargés:', edges.value.length)

    viewport.value = structure.viewport || { x: 0, y: 0, zoom: 1 }
    await nextTick()
    fitView({ padding: 0.2 })

    console.log('✅ [loadStructure] Structure chargée avec succès')
  } catch (err) {
    // Si le test n'existe pas encore en base (nouveau test non sauvegardé), on initialise une structure vide
    if (err.response?.status === 404) {
      console.warn('⚠️ [loadStructure] Test pas encore sauvegardé en base, initialisation structure vide')
      nodes.value = []
      edges.value = []
      viewport.value = { x: 0, y: 0, zoom: 1 }
      // Le watcher sur testData._id rechargera automatiquement quand le test sera sauvegardé
    } else {
      console.error('❌ Erreur chargement:', err)
      console.error('❌ Détails erreur:', err.response?.data || err.message)
    }
  } finally {
    isLoadingStructure.value = false
  }
}


// -------------------- UI: handlers VueFlow --------------------
function onConnect(params) {
  console.log('🔗 [onConnect] Connexion réussie vers un node:', params)

  // Reset connectionInProgress car la connexion a abouti
  connectionInProgress.value = null

  const edge = {
    ...params,
    id: uuidv4(),
    type: 'default',
    animated: true,
    data: { condition: { type: 'always', operator: '==', value: null, label: 'Toujours' } }
  }
  edges.value = [...edges.value, edge]
  requestSaveStructure()
}

/**
 * Gère la fin d'une tentative de connexion (quand on lâche le handle)
 * Si la connexion est lâchée dans le vide (pas de target), crée automatiquement un switcher
 */
function handleConnectEnd(event) {
  console.log('🔗 [handleConnectEnd] Événement déclenché')
  console.log('🔗 [handleConnectEnd] MouseEvent:', event)
  console.log('🔗 [handleConnectEnd] connectionInProgress:', connectionInProgress.value)

  // Si pas de connexion en cours, ignorer
  if (!connectionInProgress.value) {
    console.log('⚠️ [handleConnectEnd] Pas de connexion en cours, abandon')
    return
  }

  const { nodeId, handleId } = connectionInProgress.value

  console.log('✨ [handleConnectEnd] Connexion dans le vide détectée ! Création d\'un switcher...')
  console.log('📍 Source:', { nodeId, handleId })

  // Obtenir la position de la souris en coordonnées du canvas
  const vp = getViewport()
  const canvasX = (event.clientX - vp.x) / vp.zoom
  const canvasY = (event.clientY - vp.y) / vp.zoom

  console.log('📍 Position calculée:', { canvasX, canvasY, viewport: vp })

  // Créer le switcher node à cette position
  const switcherNode = {
    id: uuidv4(),
    type: 'switcher',
    position: { x: canvasX - 120, y: canvasY - 50 }, // Centrer sur le curseur
    style: { width: 240, height: 100, background: '#e6f0ff', border: '2px solid #0d6efd' },
    data: {
      label: 'Switcher',
      conditions: [],
      collapsed: false
    },
  }

  // Ajouter le node
  nodes.value = [...nodes.value, switcherNode]
  console.log('✅ Switcher node ajouté:', switcherNode.id)

  // Créer la connexion entre le node source et le switcher
  const edge = {
    id: uuidv4(),
    source: nodeId,
    sourceHandle: handleId,
    target: switcherNode.id,
    type: 'default',
    animated: true,
    data: { condition: { type: 'always', operator: '==', value: null, label: 'Toujours' } }
  }

  edges.value = [...edges.value, edge]
  console.log('✅ Edge ajouté:', edge.id)

  // Reset connectionInProgress
  connectionInProgress.value = null

  requestSaveStructure()
  console.log('✅ [handleConnectEnd] Switcher créé et connecté avec succès')
}

// Utiliser les hooks VueFlow pour tracker les connexions
onConnectStart((params) => {
  console.log('🔗 [onConnectStart] Connexion commencée:', params)
  connectionInProgress.value = {
    nodeId: params.nodeId,
    handleId: params.handleId,
    handleType: params.handleType
  }
})

vueFlowOnConnectEnd((event) => {
  console.log('🔗 [vueFlowOnConnectEnd] Fin de connexion')
  // Si on a une connexion en cours, c'est qu'elle a été lâchée dans le vide
  if (connectionInProgress.value) {
    handleConnectEnd(event)
  }
})

function onEdgeUpdate({ edge, connection }) {
  edges.value = edges.value
    .filter(e => e.id !== edge.id)
    .concat({ ...edge, ...connection })
}


function onNodesChange(changes) {
  // ⚠️ Bloquer UNIQUEMENT pendant le chargement, PAS pendant la sauvegarde (sinon le scroll est bloqué)
  if (isLoadingStructure.value) return

  // Intercepter les suppressions (backspace) pour supprimer aussi les éléments
  const removeChanges = changes.filter(c => c.type === 'remove')
  if (removeChanges.length > 0) {
    console.log('🗑️ [onNodesChange] Détection de suppression(s) par backspace:', removeChanges.length)

    // Supprimer les éléments associés de la base de données (de manière légère)
    removeChanges.forEach(change => {
      const nodeToDelete = nodes.value.find(n => n.id === change.id)
      if (nodeToDelete) {
        const elementId = nodeToDelete.data?.elementId
        console.log('🗑️ [onNodesChange] Node supprimé:', nodeToDelete.id, 'elementId:', elementId)

        // Ajouter à la liste des supprimés pour éviter qu'il réapparaisse
        if (!deletedNodeIds.value.includes(nodeToDelete.id)) {
          deletedNodeIds.value.push(nodeToDelete.id)
        }

        // Supprimer l'élément de la base de données (async, sans bloquer)
        if (elementId) {
          const testId = props.testData._id || props.testData.ID
          if (testId) {
            api.delete(`/tests/${testId}/elements/${elementId}`)
              .then(() => {
                console.log(`✅ [onNodesChange] Élément ${elementId} supprimé de la DB`)
                // Supprimer aussi de testData.elements
                if (testStore.testData.elements) {
                  const idx = testStore.testData.elements.findIndex(el => el.el_ID === elementId)
                  if (idx !== -1) {
                    testStore.testData.elements.splice(idx, 1)
                  }
                }
              })
              .catch(err => console.error('❌ [onNodesChange] Erreur suppression élément:', err))
          }
        }
      }
    })
  }

  // Appliquer tous les changements normalement (y compris les suppressions)
  nodes.value = applyNodeChanges(changes, nodes.value)
  const moved = changes.some(c => c.type === 'position' && c.dragging === false)
  if (moved) requestSaveStructure()
}


function onEdgesChange(changes) {
  // ⚠️ Bloquer UNIQUEMENT pendant le chargement, PAS pendant la sauvegarde (sinon le scroll est bloqué)
  if (isLoadingStructure.value) return

  edges.value = applyEdgeChanges(changes, edges.value)
  requestSaveStructure()
}

function onNodeClick({ node }) {
  selectedNode.value = node
  selectedEdge.value = null

  if (node.type === 'page') {
    // ouvrir l’éditeur de page
    selectedPageForEdit.value = node
    showPageEditor.value = true
    return
  }

  const elId = node?.data?.elementId
  const subtype = node?.data?.subtype

  if (elId && subtype) {
    const element = getElementById(elId)
    if (element) {
      testStore.selectedElement = element
      testStore.selectedElementLabel = element.el_Text || `Element ${element.el_ID}`
    }

    // Ouvrir la zone inférieure (éditeur + preview)
    activeEditor.value = { type: subtype, nodeId: node.id, elementId: elId }
  }
}

function onEdgeClick({ edge }) { selectedEdge.value = edge; selectedNode.value = null }
function onPaneClick() { clearSelection() }
function clearSelection() { selectedNode.value = null; selectedEdge.value = null }

function updateNode(payload) {
  const nodeId = payload.id
  const patch = payload.data

  if (!nodeId) {
    console.warn('[updateNode] Missing nodeId in payload:', payload)
    return
  }

  nodes.value = nodes.value.map(n => {
    if (n.id === nodeId) {
      return {
        ...n,
        data: { ...n.data, ...patch }
      }
    }
    return n
  })

  console.log(`📝 Node ${nodeId} mis à jour:`, patch)
  requestSaveStructure()
}
function updateNodeData() {
  nodes.value = [...nodes.value]
  requestSaveStructure()
}
function updateEdgeCondition(condition) {
  if (!selectedEdge.value) return
  const edge = edges.value.find(e => e.id === selectedEdge.value.id)
  if (!edge) return
  edge.data = edge.data || {}
  edge.data.condition = condition
  edge.label = condition.label
  edges.value = [...edges.value]
  requestSaveStructure()
}
function getNodeById(nodeId) { return nodes.value.find(n => n.id === nodeId) }

async function deleteNode(payload) {
  const id = payload.nodeId
  const el_Id = payload.elementId

  if (!id) {
    console.warn('[deleteNode] payload invalide:', payload)
    return
  }

  console.log(`🗑️ [deleteNode] Suppression node=${id}, element=${el_Id}`)

  const testId = props.testData._id || props.testData.ID
  if (!testId) {
    console.error('❌ [deleteNode] testId manquant')
    return
  }

  // 1️⃣ Ajouter à la liste des supprimés
  if (!deletedNodeIds.value.includes(id)) {
    deletedNodeIds.value.push(id)
  }

  // 2️⃣ Retirer le node et edges visuellement
  edges.value = edges.value.filter(e => e.source !== id && e.target !== id)
  nodes.value = nodes.value.filter(n => n.id !== id)

  // 3️⃣ Nettoyage de la sélection
  if (selectedNode.value?.id === id) selectedNode.value = null
  if (selectedEdge.value?.source === id || selectedEdge.value?.target === id) selectedEdge.value = null

  // 4️⃣ Supprimer l'élément de la base de données (async en arrière-plan)
  if (el_Id) {
    api.delete(`/tests/${testId}/elements/${el_Id}`)
      .then(() => {
        console.log(`✅ [deleteNode] Élément ${el_Id} supprimé de la DB`)
        // ⚠️ NOTE: On ne touche PAS à testData.elements pour éviter de déclencher des watchers
        // qui appelleraient loadStructure() et rechargeraient les données du backend (stale data)
        // L'élément sera nettoyé lors du prochain chargement complet du test
      })
      .catch(err => console.error('❌ [deleteNode] Erreur suppression élément:', err))
  }

  // 5️⃣ Sauvegarder la structure (debounced)
  requestSaveStructure()

  console.log(`✅ [deleteNode] Node ${id} supprimé`)
}




// -------------------- Validation & tools --------------------
async function validateGraph() {
  const testId = props.testData._id || props.testData.ID
  try {
    const response = await api.post(
      `/tests/${testId}/validate-conditional`,
      {
        nodes: nodes.value,
        edges: edges.value,
        startNode: startNodeId.value,
        terminationNodes: terminationNodeIds.value
      }
    )
    validationResults.value = response.data
  } catch (error) {
    console.error('Validation error:', error)
    alert('Erreur lors de la validation')
  }
}

function resetZoom() { zoomTo(1, { duration: 300 }) }

function autoLayout() {
  const startNodes = nodes.value.filter(n => n.type === 'start')
  const questionNodes = nodes.value.filter(n => toPersistType(n.type) === 'question')
  const terminationNodes = nodes.value.filter(n => n.type === 'termination')

  let y = 50
  startNodes.forEach((n, i) => { n.position = { x: 50, y: y + i * 150 } })
  questionNodes.forEach((n, i) => { n.position = { x: 300, y: y + i * 150 } })
  terminationNodes.forEach((n, i) => { n.position = { x: 550, y: y + i * 150 } })
  nodes.value = [...nodes.value]

  try { fitView({ padding: 0.2, duration: 500 }) } catch { }
  requestSaveStructure()
}

// -------------------- Nettoyage des nodes non connectés --------------------
async function cleanUnconnectedNodes() {
  const connectedNodeIds = new Set()

  // Identifier tous les nodes connectés
  edges.value.forEach(edge => {
    connectedNodeIds.add(edge.source)
    connectedNodeIds.add(edge.target)
  })

  // Trouver les nodes non connectés (en gardant le start node)
  const unconnectedNodes = nodes.value.filter(n => !connectedNodeIds.has(n.id) && n.type !== 'start')

  if (unconnectedNodes.length === 0) {
    alert('✅ Aucun nœud non connecté à supprimer.')
    return
  }

  // Confirmation
  const confirmMessage = `⚠️ Voulez-vous supprimer ${unconnectedNodes.length} nœud(s) non connecté(s) ?\n\nLes nœuds suivants seront supprimés :\n${unconnectedNodes.map(n => `- ${n.data?.label || n.type}`).join('\n')}\n\nCette action est irréversible.`

  if (!window.confirm(confirmMessage)) {
    return
  }

  // Supprimer les nodes non connectés
  const unconnectedNodeIds = unconnectedNodes.map(n => n.id)
  nodes.value = nodes.value.filter(n => !unconnectedNodeIds.includes(n.id))

  // Sauvegarder
  await saveStructure()

  console.log(`✅ ${unconnectedNodes.length} nœud(s) non connecté(s) supprimé(s)`)
  alert(`✅ ${unconnectedNodes.length} nœud(s) non connecté(s) supprimé(s) avec succès.`)
}


// -------------------- Computed --------------------
const choiceElements = computed(() =>
  (props.testData.elements || []).filter(el => el.el_Type === 'choice')
)
const hasStartNode = computed(() => nodes.value.some(n => n.type === 'start'))
const startNodeId = computed(() => nodes.value.find(n => n.type === 'start')?.id || null)
const terminationNodeIds = computed(() => nodes.value.filter(n => n.type === 'termination').map(n => n.id))

// Calcul des nodes non connectés
const unconnectedNodesCount = computed(() => {
  const connectedNodeIds = new Set()

  // Parcourir toutes les edges pour identifier les nodes connectés
  edges.value.forEach(edge => {
    connectedNodeIds.add(edge.source)
    connectedNodeIds.add(edge.target)
  })

  // Compter les nodes non connectés (en excluant le node start qui peut être seul)
  return nodes.value.filter(n => !connectedNodeIds.has(n.id) && n.type !== 'start').length
})

// -------------------- Lifecycle --------------------
onMounted(loadStructure)
watch(() => props.testData._id || props.testData.ID, loadStructure)

// Bloquer le scroll du body quand la modale est ouverte
watch([showCreateModal, showPageEditor], ([createModal, pageEditor]) => {
  if (createModal || pageEditor) {
    // Bloquer le scroll
    document.body.style.overflow = 'hidden'
  } else {
    // Restaurer le scroll
    document.body.style.overflow = ''
  }
})

// ⚠️ WATCHER DÉSACTIVÉ : Il causait des conflits lors de la suppression de nodes
// Le watcher écrasait nodes.value avec les données du store, faisant réapparaître les nodes supprimés
// L'éditeur gère maintenant ses nodes/edges de manière autonome via loadStructure() explicite
/*
watch(
  () => testStore.testData?.ConditionalStructure,
  (s) => {
    // ⚠️ Ignorer les mises à jour pendant qu'on sauvegarde ou charge
    if (isLoadingStructure.value || isSavingStructure.value) {
      console.log('🟡 [watcher ConditionalStructure] Ignoré car opération en cours')
      return
    }

    if (!s) return

    const allNodes = s.nodes || []
    const allEdges = s.edges || []

    // 🗑️ Filtrer les nodes supprimés (cachés définitivement)
    const nodes = allNodes.filter(n => !deletedNodeIds.value.includes(n.id))

    if (allNodes.length !== nodes.length) {
      console.log(`🗑️ [watcher] ${allNodes.length - nodes.length} node(s) supprimé(s) filtré(s)`, deletedNodeIds.value)
    }

    // 🧩 Filtrer les edges invalides avant de les passer à Vue Flow
    const nodeIds = new Set(nodes.map(n => n.id))
    const validEdges = allEdges.filter(
      e => e && nodeIds.has(e.source) && nodeIds.has(e.target)
    )

    if (allEdges.length !== validEdges.length) {
      console.warn(
        `🧹 ${allEdges.length - validEdges.length} edges supprimées car source/target manquante`
      )
    }

    console.log('📊 [watcher ConditionalStructure] Mise à jour nodes/edges depuis store')
    setNodes(nodes)
    setEdges(validEdges)
  },
  { deep: true, immediate: true }
)
*/




</script>




<style scoped>
.conditional-editor {
  width: 100%;
  height: calc(100vh - 200px);
  /* Hauteur de la fenêtre moins header/nav */
  min-height: 600px;
  display: flex;
  flex-direction: column;
  background: #f8f9fa;
}

.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: white;
  border-bottom: 2px solid #dee2e6;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.toolbar-section {
  display: flex;
  align-items: center;
}

.toolbar-actions {
  gap: 0.5rem;
}

.toolbar-divider {
  width: 1px;
  height: 30px;
  background: #dee2e6;
  margin: 0 0.5rem;
}

.flow-container {
  flex: 1;
  position: relative;
  min-height: 400px;
}

/* Custom Zoom Controls */
.custom-controls {
  position: absolute;
  bottom: 20px;
  left: 20px;
  z-index: 10;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: white;
  padding: 8px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.control-btn {
  width: 36px;
  height: 36px;
  border: 1px solid #dee2e6;
  background: white;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #495057;
  font-size: 14px;
}

.control-btn:hover {
  background: #f8f9fa;
  border-color: #0d6efd;
  color: #0d6efd;
  transform: scale(1.05);
}

.control-btn:active {
  transform: scale(0.95);
}

.inspector-panel {
  position: absolute;
  top: 0;
  right: 0;
  width: 350px;
  height: 100%;
  background: white;
  border-left: 2px solid #dee2e6;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.1);
  z-index: 10;
  display: flex;
  flex-direction: column;
}

.inspector-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid #dee2e6;
  background: #f8f9fa;
}

.inspector-content {
  flex: 1;
  padding: 1rem;
  overflow-y: auto;
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(100%);
}

/* Vue Flow custom styles */
:deep(.vue-flow__node) {
  cursor: pointer;
}

:deep(.vue-flow__edge) {
  cursor: pointer;
}

:deep(.vue-flow__edge-path) {
  stroke-width: 2;
}

:deep(.vue-flow__edge.selected .vue-flow__edge-path) {
  stroke: #0d6efd;
  stroke-width: 3;
}

/* Minimap positioning */
:deep(.vue-flow__minimap) {
  z-index: 5;
  bottom: 20px;
  right: 20px;
}

/* Bottom panel for editor and preview */
.bottom-panel {
  border-top: 2px solid #dee2e6;
  background: white;
  max-height: 50vh;
  display: flex;
  flex-direction: column;
}

.bottom-panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.bottom-panel-content {
  flex: 1;
  overflow-y: auto;
  max-height: calc(50vh - 50px);
}

.editor-column,
.preview-column {
  padding: 1rem;
  overflow-y: auto;
  height: 100%;
}

.editor-column {
  border-right: 1px solid #dee2e6;
  background: #f8f9fa;
}

.preview-column {
  background: white;
}

.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, .45);
  display: grid;
  place-items: center;
  z-index: 9999;
  overflow: hidden; /* Empêche le scroll du backdrop */
}

.modal-card {
  background: #fff;
  width: min(720px, 92vw);
  max-height: 90vh; /* Limiter la hauteur */
  border-radius: 12px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, .25);
  overflow: hidden;
  display: flex;
  flex-direction: column; /* Pour que modal-body puisse scroller */
}

.modal-card.modal-lg {
  width: min(1000px, 95vw);
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.modal-card.modal-lg .modal-body {
  overflow-y: auto;
  flex: 1;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid #eee;
  flex-shrink: 0; /* Ne pas réduire le header */
}

.modal-body {
  padding: 16px;
  overflow-y: auto; /* Permettre le scroll uniquement dans le body */
  flex: 1; /* Prendre l'espace restant */
}

.btn-close {
  background: none;
  border: 0;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
}
.ctrlScroll{
  color: grey;
  font-size: smaller;
}
</style>
